import { datadogRum } from "@datadog/browser-rum";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getXSRFToken, requestLogin, requestVerifyTwoFactor, type TwoFactorChallengeBody } from "api/user.api";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { formatServerError } from "utils/format";
import { pickTranslatedAuthError } from "utils/loginAuthErrors";
import * as yup from "yup";
import { ObjectSchema } from "yup";

type LoginFormModel = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type VerifyFormModel = {
  code: string;
  rememberDevice: boolean;
};

export type LoginPhase = "credentials" | "two-factor";

export const useLogin = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("error");
  const { t: tUi } = useTranslation();

  const [phase, setPhase] = useState<LoginPhase>("credentials");
  const [challenge, setChallenge] = useState<TwoFactorChallengeBody | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; rememberMe: boolean } | null>(null);
  const [credentialsBanner, setCredentialsBanner] = useState<string | null>(null);

  const schema: ObjectSchema<LoginFormModel> = yup.object().shape({
    email: yup.string().email(t("yup.email.badFormat")).required(t("yup.email.required")),
    password: yup.string().required(t("yup.password.required")),
    rememberMe: yup.boolean().default(false).optional(),
  });

  const verifySchema: ObjectSchema<VerifyFormModel> = yup.object().shape({
    code: yup.string().required(tUi("login.two-factor.code-required")),
    rememberDevice: yup.boolean().default(false).optional(),
  });

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<LoginFormModel>({
    resolver: yupResolver(schema),
  });

  const {
    register: registerCode,
    handleSubmit: handleSubmitCode,
    resetField: resetCodeField,
    formState: { errors: codeErrors },
  } = useForm<VerifyFormModel>({
    resolver: yupResolver(verifySchema),
    defaultValues: { code: "", rememberDevice: false },
  });

  const navigateAfterSignIn = useCallback(
    async (email: string) => {
      datadogRum.setUser({ email });
      datadogRum.addAction("login-success", { email });
      queryClient.removeQueries(); // invalidate all new login
      const from = (location.state as { from: Location })?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    },
    [location.state, navigate, queryClient]
  );

  const {
    isPending: isLoginPending,
    error: loginAxiosError,
    mutate: mutateLogin,
    reset: resetLoginMutation,
  } = useMutation<void, AxiosError, LoginFormModel>({
    mutationKey: ["user-login"],
    mutationFn: async ({ email, password, rememberMe }) => {
      const result = await requestLogin(email, password, rememberMe);
      if (result.outcome === "session") {
        await navigateAfterSignIn(email);
        return;
      }
      setChallenge(result.challenge);
      setPendingCredentials({ email, rememberMe });
      setPhase("two-factor");
    },
    retry: false,
    onError: (error: AxiosError, { email }: LoginFormModel) => {
      resetField("password");
      if (error.response?.status === 401) {
        datadogRum.addAction("login-failed", {
          email,
          error: {
            status: error.response?.status,
            message: error.message,
            response: error.response?.data,
          },
        });
      }
    },
  });

  const {
    isPending: isVerifyPending,
    error: verifyAxiosError,
    mutate: mutateVerify,
    reset: resetVerifyMutation,
  } = useMutation<void, AxiosError, { pendingToken: string; code: string; email: string; rememberDevice: boolean }>({
    mutationKey: ["user-login-verify-2fa"],
    mutationFn: async ({ pendingToken, code, rememberDevice }) => {
      await requestVerifyTwoFactor(pendingToken, code, rememberDevice);
    },
    retry: false,
    onSuccess: async (_: void, { email }) => {
      datadogRum.addAction("two-factor-verify-success", { email });
      await navigateAfterSignIn(email);
    },
    onError: (err: AxiosError, { email }) => {
      resetCodeField("code");
      if (err.response?.status === 400) {
        datadogRum.addAction("two-factor-verify-failed", {
          email,
          error: {
            status: err.response?.status,
            message: err.message,
            response: err.response?.data,
          },
        });
      }
      if (err.response?.status === 401) {
        const msg = pickTranslatedAuthError(err.response.data, t) ?? t("login.two-factor.generic-error");
        setCredentialsBanner(msg);
        setPhase("credentials");
        setChallenge(null);
        setPendingCredentials(null);
        resetVerifyMutation();
        resetLoginMutation();
      }
    },
  });

  useEffect(() => {
    datadogRum.addAction("fetch-XSRF");
    getXSRFToken().catch(() => Promise.resolve());
  }, []);

  const resolveServerError = useCallback(
    (axiosError: AxiosError | null | undefined) => {
      return formatServerError(axiosError ?? null, (axiosResponse) => {
        const data = axiosResponse.data;
        const mapped = pickTranslatedAuthError(data, t);
        if (mapped) {
          return mapped;
        }
        if (axiosResponse.status === 401) {
          return t("login.credentials");
        }
        if (axiosResponse.status === 400 || axiosResponse.status === 503 || axiosResponse.status === 502) {
          return t("common.server");
        }
        return null;
      });
    },
    [t]
  );

  const axiosErrorForDisplay = phase === "two-factor" ? verifyAxiosError : loginAxiosError;

  const serverError = resolveServerError(axiosErrorForDisplay);
  const displayError = credentialsBanner ?? serverError;

  const onSubmitCredentials: SubmitHandler<LoginFormModel> = async ({ email, password, rememberMe }) => {
    setCredentialsBanner(null);
    mutateLogin({ email, password, rememberMe });
  };

  const onSubmitVerify: SubmitHandler<VerifyFormModel> = async ({ code, rememberDevice }) => {
    if (!challenge || !pendingCredentials) {
      return;
    }
    mutateVerify({
      pendingToken: challenge.pendingToken,
      code,
      email: pendingCredentials.email,
      rememberDevice: pendingCredentials.rememberMe ? false : rememberDevice,
    });
  };

  const backToCredentials = () => {
    setCredentialsBanner(null);
    setPhase("credentials");
    setChallenge(null);
    setPendingCredentials(null);
    resetLoginMutation();
    resetVerifyMutation();
    resetCodeField("code");
  };

  const showRememberDevice = phase === "two-factor" && !pendingCredentials?.rememberMe;

  return {
    phase,
    challenge,
    isLoginPending,
    isVerifyPending,
    serverError: displayError,
    errors,
    codeErrors,
    register,
    registerCode,
    login: handleSubmit(onSubmitCredentials),
    verify: handleSubmitCode(onSubmitVerify),
    backToCredentials,
    showRememberDevice,
  };
};
